import { useMemo, useState, useEffect, useCallback, useRef } from 'react'
import { FacturaAPI } from '../../../../../../../api/facturas.api'
import { useGlobalContext } from '../../../../../../../context/GlobalState'
import AdminDocsTable from './AdminDocsTable'
import AdminDocsFilters from './AdminDocsFilters'
import DocumentDetailView from './DocumentDetailView'
import CreditNotePreviewView from './CreditNotePreviewView'
import FirmaNotaCredito from './FirmaNotaCredito'

export default function AdminDocsIndex({ onBack, onEmitSuccess }) {
  const { currentBusiness, masterEntityData } = useGlobalContext()
  const api = useMemo(() => new FacturaAPI(), [])

  const [isLoading, setIsLoading] = useState(false)
  const masterEntityId = masterEntityData?.id ? Number(masterEntityData.id) : null
  const [rows, setRows] = useState([])

  // Step: 1 listado, 2 detalle PDF, 3 preview NC, 4 Firma NC
  const [step, setStep] = useState(1)
  const [selectedDoc, setSelectedDoc] = useState(null)
  const [ncPayload, setNcPayload] = useState(null)

  const [filters, setFilters] = useState({
    rutReceptor: '',
    folio: '',
    razonSocial: '',
    fechaDesde: '',
    fechaHasta: '',
    tipoDocumento: '',
    estado: ''
  })
  const [appliedFilters, setAppliedFilters] = useState(filters)

  // ✅ cache de detalles por doc (id)
  const detailsRef = useRef({}) // key -> { status, pdf, xml, fetchedAt, error }
  const [detailsByKey, setDetailsByKey] = useState({})
  const [loadingActionByKey, setLoadingActionByKey] = useState({}) // key -> 'pdf'

  // para evitar races
  const requestIdRef = useRef(0)

  const getRowKey = useCallback(row => String(row?.id ?? ''), [])

  // 1) master_entity_id now comes from GlobalContext
  // No local fetch logic needed here anymore.

  // 2) cargar emitidos (SOLO LISTADO, sin detalle por folio)
  const fetchIssuedDocs = useCallback(async () => {
    const masterId = Number(masterEntityId)
    if (!masterId) return

    const currentReqId = ++requestIdRef.current
    setIsLoading(true)

    try {
      const res = await api.getDocuments({
        master_entity_id: masterId,
        document_type: 'issued',
        page: 1,
        page_size: 200
      })

      if (requestIdRef.current !== currentReqId) return

      const list = res?.data?.results || []
      setRows(list)
    } catch (e) {
      console.error('ADMIN: Error getDocuments issued', e)
      setRows([])
    } finally {
      if (requestIdRef.current === currentReqId) setIsLoading(false)
    }
  }, [api, masterEntityId])

  useEffect(() => {
    if (!masterEntityId) return
    fetchIssuedDocs()
  }, [fetchIssuedDocs, masterEntityId])

  // ✅ Lazy: pedir detalle SOLO si se necesita
  const fetchDetailIfNeeded = useCallback(
    async row => {
      const key = getRowKey(row)
      if (!key) return null

      const cached = detailsRef.current[key]
      if (cached?.status === 'success') return cached

      // marco loading en cache + state
      detailsRef.current[key] = { status: 'loading', pdf: null, xml: null }
      setDetailsByKey(prev => ({ ...prev, [key]: detailsRef.current[key] }))

      try {
        const masterId = Number(masterEntityId)
        if (!masterId) throw new Error('master_entity_id inválido')

        const det = await api.getDocuments({
          master_entity_id: masterId,
          document_type: 'issued',
          folio: row.folio
        })

        const results = det?.data?.results || []

        // ✅ importante: folio puede repetirse; preferimos match por id
        const hit = results.find(r => String(r.id) === String(row.id)) || results[0] || {}

        const next = {
          status: 'success',
          pdf: hit.pdf ?? null,
          xml: hit.xml ?? null,
          fetchedAt: Date.now()
        }

        detailsRef.current[key] = next
        setDetailsByKey(prev => ({ ...prev, [key]: next }))
        return next
      } catch (err) {
        const next = { status: 'error', pdf: null, xml: null, error: err?.message || 'error' }
        detailsRef.current[key] = next
        setDetailsByKey(prev => ({ ...prev, [key]: next }))
        return next
      }
    },
    [api, masterEntityId, getRowKey]
  )

  // 3) filtrar frontend (igual)
  const filteredRows = useMemo(() => {
    const termRut = (appliedFilters.rutReceptor || '').replace(/\./g, '').toLowerCase().trim()
    const termFolio = String(appliedFilters.folio || '').trim()
    const termRazon = (appliedFilters.razonSocial || '').toLowerCase().trim()
    const tipo = String(appliedFilters.tipoDocumento || '').trim()
    const estado = appliedFilters.estado

    const dFrom = appliedFilters.fechaDesde ? new Date(appliedFilters.fechaDesde).getTime() : null
    const dTo = appliedFilters.fechaHasta ? new Date(appliedFilters.fechaHasta).getTime() : null

    return (rows || []).filter(r => {
      if (termRut) {
        const rr = String(r.receiver_tax_id || '')
          .replace(/\./g, '')
          .toLowerCase()
        if (!rr.includes(termRut)) return false
      }
      if (termFolio) {
        if (String(r.folio || '') !== termFolio) return false
      }
      if (termRazon) {
        const name = String(r.receiver_name || '').toLowerCase()
        if (!name.includes(termRazon)) return false
      }
      if (tipo) {
        const code = String(r?.dte_type_code ?? r?.dte_type?.code ?? '')
        if (code !== tipo) return false
      }
      if (estado === 'paid' && !r.is_paid) return false
      if (estado === 'unpaid' && r.is_paid) return false

      const t = r.date_issued ? new Date(r.date_issued).getTime() : null
      if (dFrom && t && t < dFrom) return false
      if (dTo && t && t > dTo) return false

      return true
    })
  }, [rows, appliedFilters])

  // ✅ Handler optimizado: al click del PDF -> trae detalle (si falta) -> step 2
  const onSelectDoc = useCallback(
    async (row, actionType = 'pdf') => {
      const key = getRowKey(row)
      if (!key) return

      // loader solo en ese botón
      setLoadingActionByKey(prev => ({ ...prev, [key]: actionType }))

      try {
        const detail = await fetchDetailIfNeeded(row)

        const enriched = {
          ...row,
          pdf: detail?.pdf ?? null,
          xml: detail?.xml ?? null
        }

        setSelectedDoc(enriched)

        if (actionType === 'nt') {
          setStep(3)
        } else {
          setStep(2)
        }
      } finally {
        setLoadingActionByKey(prev => {
          const copy = { ...prev }
          delete copy[key]
          return copy
        })
      }
    },
    [getRowKey, fetchDetailIfNeeded]
  )

  const onBackStep = () => {
    if (step === 1) return onBack?.()
    if (step === 2) {
      setStep(1)
      setSelectedDoc(null)
      return
    }
    if (step === 3) {
      setStep(1)
      setSelectedDoc(null)
      return
    }
    if (step === 4) {
      setStep(3)
      return
    }
  }

  // Render steps
  if (step === 2 && selectedDoc) {
    return (
      <DocumentDetailView
        currentBusiness={currentBusiness}
        doc={selectedDoc}
        onBack={onBackStep}
        onGenerateVoidNC={() => setStep(3)}
      />
    )
  }

  if (step === 3 && selectedDoc) {
    return (
      <CreditNotePreviewView
        currentBusiness={currentBusiness}
        baseDoc={selectedDoc}
        onBack={onBackStep}
        onConfirm={payload => {
          setNcPayload(payload)
          setStep(4)
        }}
      />
    )
  }

  if (step === 4 && selectedDoc && ncPayload) {
    return (
      <FirmaNotaCredito
        document={ncPayload}
        baseDoc={selectedDoc}
        currentBusiness={currentBusiness}
        onBack={onBackStep}
        onEmitSuccess={onEmitSuccess}
      />
    )
  }

  // Step 1: listado + filtros
  return (
    <div className='space-y-4'>
      <div className='bg-white p-6 rounded-2xl shadow'>
        <div className='flex items-start justify-between gap-4'>
          <div>
            <h2 className='text-2xl font-semibold'>ADMINISTRACIÓN DE DOCUMENTOS EMITIDOS</h2>
            <p className='text-sm text-gray-600 mt-1'>
              En esta página un usuario autorizado puede revisar, ordenar, buscar, listar y obtener
              copias de documentos emitidos.
            </p>
          </div>

          <div className='text-sm text-gray-700'>
            <span className='mr-2'>Empresa:</span>
            <span className='font-semibold'>{currentBusiness?.rut || '-'}</span>
          </div>
        </div>

        <div className='mt-4'>
          <AdminDocsTable
            rows={filteredRows}
            isLoading={isLoading}
            onSelectDoc={onSelectDoc}
            // ✅ props para loader por fila
            getRowKey={getRowKey}
            detailsByKey={detailsByKey}
            loadingActionByKey={loadingActionByKey}
          />
        </div>

        <div className='mt-6'>
          <AdminDocsFilters
            filters={filters}
            setFilters={setFilters}
            onApply={() => setAppliedFilters(filters)}
            onClear={() => {
              const clean = {
                rutReceptor: '',
                folio: '',
                razonSocial: '',
                fechaDesde: '',
                fechaHasta: '',
                tipoDocumento: '',
                estado: ''
              }
              setFilters(clean)
              setAppliedFilters(clean)
            }}
          />
        </div>

        <button type='button' onClick={onBackStep} className='cursor-pointer rounded-lg bg-primary px-6 py-2.5 text-sm font-bold text-white shadow-lg shadow-black/20 transition-all hover:bg-primary/90 active:scale-95  mt-6'>
          Volver Menú
        </button>
      </div>
    </div>
  )
}

